import { useApolloClient } from "@apollo/client/react";
import { Contacts, type GQL } from "@binale-tech/shared";
import { use, useCallback, useMemo } from "react";

import { bankingTransactionSetDmsDocumentIds } from "@domain/context/mutations/bankMutations.graphql";
import { type AiPaymentMethod } from "@dms/components/Bewirtung/aiLogSource";
import { DocumentsApi } from "@domain/api/DocumentsApi/DocumentsApi";
import { dmsBewirtungSave } from "@domain/context/mutations/documentMutations.graphql";
import { BankTransactionsContext } from "@domain/context/BanksContext";
import { useDmsDocumentsMap, useDmsTypes } from "@domain/context/DmsDataContexts";
import { DmsType } from "@domain/models/dms";
import { useToastSaver } from "@ui-components/hooks/useToastSaver";

export type AssistantActions = {
    saveBewirtung: (documentId: string, bewirtung: GQL.IDmsBewirtungInput) => Promise<boolean>;
    moveTo: (documentId: string, type: DmsType, subType: string | null) => Promise<boolean>;
    linkAndMoveToBank: (
        documentId: string,
        transactionId: string,
        aiPaymentMethod: AiPaymentMethod | null,
    ) => Promise<boolean>;
};

export const useAssistantActions = (): AssistantActions => {
    const client = useApolloClient();
    const toastSaver = useToastSaver();
    const documentsMap = useDmsDocumentsMap();
    const { transactionMap } = use(BankTransactionsContext);
    const { documentTypes } = useDmsTypes();

    const getDocument = useCallback(
        (documentId: string): GQL.IDocument => {
            const document = documentsMap.get(documentId);
            if (!document) {
                throw new Error(`Document ${documentId} not found`);
            }
            return document;
        },
        [documentsMap],
    );

    const save = useCallback(
        async (fn: () => Promise<void>): Promise<boolean> =>
            (await toastSaver(async () => {
                await fn();
                return true;
            })) === true,
        [toastSaver],
    );

    const saveBewirtung = useCallback<AssistantActions["saveBewirtung"]>(
        (documentId, bewirtung) =>
            save(async () => {
                const { companyId } = getDocument(documentId);
                await client.mutate({
                    mutation: dmsBewirtungSave,
                    variables: { companyId, documentId, input: bewirtung },
                });
            }),
        [client, getDocument, save],
    );

    const moveTo = useCallback<AssistantActions["moveTo"]>(
        (documentId, type, subType) =>
            save(async () => {
                await DocumentsApi.changeDocumentsType([{ ...getDocument(documentId), type, subType }], client);
            }),
        [client, getDocument, save],
    );

    const linkAndMoveToBank = useCallback<AssistantActions["linkAndMoveToBank"]>(
        (documentId, transactionId, aiPaymentMethod) =>
            save(async () => {
                const document = getDocument(documentId);
                const transaction = transactionMap.get(transactionId);
                if (!transaction) {
                    throw new Error(`Transaction ${transactionId} not found`);
                }
                // Same rule as the sorter: the Avis reconcile inside the link writes Überweisung on an empty paymentType.
                if (!document.paymentType && aiPaymentMethod === "card") {
                    const paymentType = Contacts.ContactConstants.PaymentTermTypes.Kartenzahlung;
                    await DocumentsApi.updateDocuments({ documents: [{ ...document, paymentType }], client });
                }
                // The mutation replaces the list, so documents linked since the sort run are sent along.
                const ids = (transaction.documents ?? []).map(({ id }) => id);
                if (!ids.includes(documentId)) {
                    ids.push(documentId);
                }
                await client.mutate({
                    mutation: bankingTransactionSetDmsDocumentIds,
                    variables: {
                        id: { id: transaction.id, companyId: transaction.companyId },
                        bankId: transaction.bankId,
                        documents: ids.map((id) => ({ id })),
                    },
                });
                const bankSubTypes = documentTypes.find(({ id }) => id === DmsType.Bank)?.subTypes ?? [];
                const subType =
                    bankSubTypes.find((s) => s.bankingBankId && s.bankingBankId === transaction.bankId)?.id ?? null;
                await DocumentsApi.changeDocumentsType([{ ...document, type: DmsType.Bank, subType }], client);
            }),
        [client, documentTypes, getDocument, save, transactionMap],
    );

    return useMemo(() => ({ saveBewirtung, moveTo, linkAndMoveToBank }), [saveBewirtung, moveTo, linkAndMoveToBank]);
};
